import React, { useContext, useMemo, useState } from 'react'
import { useQuery } from '@apollo/client'
import UserContext from '../context/UserContext'
import {
  MY_STUDENTS_QUERY, MY_ASSIGNED_LESSONS_QUERY, DRILL_RESULTS_FOR_ME_QUERY, LESSON_WORK_FOR_ME_QUERY, MY_TEACH_CODE_QUERY,
} from '../query/gqlQueries'
import languages from '../languages'
import StudentDashboard from './StudentDashboard'
import { openHomeworkOn } from '../dialog/HomeworkDialog'
import styles from './HomeView.module.scss'
import d from './Dashboard.module.scss'
import { Shelf } from './WorkHome'

// Teach: for anyone with students. What needs you first — answers waiting
// for your feedback — then your students (each opens their dashboard), the
// homework you've set and how far each class has got, what students have
// shared with you, and your teaching code for a new student. The same
// teacher's tools as Homework → My students, laid out as a home.

const when = (v) => { const t = v ? new Date(v) : null; return t && !isNaN(t) ? t.toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : '' }
const langList = (codes) => (codes || []).map(c => languages[c] || c).join(', ')

export default function TeachHome({ run }) {
  const { username } = useContext(UserContext)
  const opts = { variables: {}, skip: !username, fetchPolicy: 'cache-and-network', errorPolicy: 'ignore' }
  const studentsQ = useQuery(MY_STUDENTS_QUERY, opts)
  const setQ = useQuery(MY_ASSIGNED_LESSONS_QUERY, opts)
  const drillsQ = useQuery(DRILL_RESULTS_FOR_ME_QUERY, { ...opts, variables: { limit: 30 } })
  const workQ = useQuery(LESSON_WORK_FOR_ME_QUERY, { ...opts, variables: { limit: 30 } })
  const codeQ = useQuery(MY_TEACH_CODE_QUERY, opts)
  const [student, setStudent] = useState(null)

  const students = studentsQ.data?.myStudents || []
  const set = useMemo(() => setQ.data?.myAssignedLessons || [], [setQ.data])
  const drills = drillsQ.data?.drillResultsForMe || []
  const work = workQ.data?.lessonWorkForMe || []

  // Waiting for you: submitted homework not yet reviewed, and shared work with no note from you.
  const waiting = useMemo(() => {
    const out = []
    for (const l of set) for (const s of l.students || []) {
      if (s.status === 'submitted' && !s.submission?.reviewedAt) out.push({ kind: 'Homework', who: s.student, title: l.title, at: s.submission?.submittedAt })
    }
    for (const w of work) if (!w.reviewedAt) out.push({ kind: 'Lesson shared', who: w.student, title: w.title, at: w.sharedAt })
    for (const r of drills) if (!r.reviewedAt) out.push({ kind: `Practice · ${r.pct}%`, who: r.student, title: r.title, at: r.sharedAt })
    return out.sort((a, b) => String(b.at || '').localeCompare(String(a.at || '')))
  }, [set, work, drills])

  // Per student: what's still to do from you, and what they've sent.
  const perStudent = useMemo(() => {
    const m = new Map(students.map(s => [s.username, { toDo: 0, waiting: 0 }]))
    for (const l of set) for (const s of l.students || []) {
      const e = m.get(s.student); if (!e) continue
      if (s.status === 'assigned') e.toDo += 1
    }
    for (const w of waiting) { const e = m.get(w.who); if (e) e.waiting += 1 }
    return m
  }, [students, set, waiting])

  if (student) {
    return <StudentDashboard student={student} onClose={() => { setStudent(null); setQ.refetch() }}
      onOpenImport={() => { setStudent(null); run('importLesson') }} />
  }

  const first = waiting[0]
  return (
    <div className={styles.grid}>
      {/* ── what needs you ── */}
      <section className={`${styles.card} ${styles.cardNext} ${d.hero}`}>
        <div className={styles.cardHead}><span className={styles.cardLabel}>Needs you</span>
          <button className={styles.linkBtn} onClick={() => openHomeworkOn('teach', () => run('homework'))}>All homework →</button></div>
        <div className={d.heroBody}>
          <div>
            <div className={d.heroTitle}>{waiting.length === 0 ? 'Nothing waiting for feedback.' : `${waiting.length} waiting for your feedback`}</div>
            <div className={d.heroMeta}>{first ? `${first.who} · ${first.kind} · ${first.title}${first.at ? ` · ${when(first.at)}` : ''}` : students.length ? 'Set some homework, or look in on a student.' : 'Share your teaching code — your students appear here.'}</div>
          </div>
          <button className={d.primary} onClick={() => (waiting.length ? openHomeworkOn('teach', () => run('homework')) : run('importLesson'))}>
            {waiting.length ? 'Give feedback →' : 'Make a lesson'}
          </button>
        </div>
      </section>

      {/* ── the counts ── */}
      <div className={`${d.stats} ${styles.cardWide}`}>
        <div className={d.stat}><b>{students.length}</b><span>Students</span></div>
        <div className={d.stat}><b>{set.length}</b><span>Homework set</span></div>
        <div className={`${d.stat} ${waiting.length ? d.statHot : ''}`}><b>{waiting.length}</b><span>Waiting for you</span></div>
        <div className={d.stat}><b>{codeQ.data?.myTeachCode || '—'}</b><span>Your teaching code</span></div>
      </div>

      {students.length > 0 && (
        <div className={styles.cardWide}>
          <Shelf title="Your students">
            {students.map(s => {
              const e = perStudent.get(s.username) || {}
              return (
                <button key={s.username} className={d.tile} onClick={() => setStudent(s.username)}>
                  <span className={d.tileTop}>
                    {e.waiting > 0 && <span className={`${d.tag} ${d.tagHot}`}>{e.waiting} to look at</span>}
                    {e.toDo > 0 && <span className={d.tag}>{e.toDo} homework to do</span>}
                  </span>
                  <span className={d.tileTitle}>{s.username}</span>
                  <span className={d.tileMeta}>{s.langs?.length ? langList(s.langs) : 'No language shared yet'}{s.since ? ` · since ${when(s.since)}` : ''}</span>
                </button>
              )
            })}
          </Shelf>
        </div>
      )}

      {set.length > 0 && (
        <div className={styles.cardWide}>
          <Shelf title="Homework you've set" action={{ label: 'All →', onClick: () => openHomeworkOn('teach', () => run('homework')) }}>
            {set.map(l => {
              const total = (l.students || []).length
              const done = (l.students || []).filter(s => s.status !== 'assigned').length
              return (
                <button key={l.meetingId} className={d.tile} onClick={() => openHomeworkOn('teach', () => run('homework'))}>
                  <span className={d.tileTitle}>{l.title}</span>
                  <span className={d.tileMeta}>{done}/{total} handed in{l.due ? ` · due ${when(l.due)}` : ''}</span>
                  <span className={d.meter}><span className={d.meterDone} style={{ width: `${total ? (done / total) * 100 : 0}%` }} /></span>
                </button>
              )
            })}
          </Shelf>
        </div>
      )}

      {(drills.length > 0 || work.length > 0) && (
        <div className={styles.cardWide}>
          <Shelf title="Shared with you" action={{ label: 'All →', onClick: () => openHomeworkOn('teach', () => run('homework')) }}>
            {[...work.map(w => ({ id: `w${w.id}`, who: w.student, title: w.title, meta: `Lesson · ${langList([w.language])}`, at: w.sharedAt, done: !!w.reviewedAt })),
              ...drills.map(r => ({ id: `r${r.id}`, who: r.student, title: r.title, meta: `Practice · ${r.pct}%`, at: r.sharedAt, done: !!r.reviewedAt }))]
              .sort((a, b) => String(b.at || '').localeCompare(String(a.at || ''))).slice(0, 12)
              .map(x => (
                <button key={x.id} className={d.tile} onClick={() => openHomeworkOn('teach', () => run('homework'))}>
                  <span className={d.tileTop}><span className={d.tag}>{x.who}</span>{!x.done && <span className={`${d.tag} ${d.tagHot}`}>New</span>}</span>
                  <span className={d.tileTitle}>{x.title}</span>
                  <span className={d.tileMeta}>{x.meta}{x.at ? ` · ${when(x.at)}` : ''}</span>
                </button>
              ))}
          </Shelf>
        </div>
      )}
    </div>
  )
}
